/**
 * Compile-time citation normalizer for the llmwiki compile pipeline.
 *
 * The LLM occasionally emits a bare line number as a citation marker,
 * e.g. `^[81]` instead of the required `^[filename.md:81]`. This module
 * repairs or removes such markers BEFORE the page body is written to disk,
 * so bad markers never reach the viewer (which would render a
 * "Source not found: 81" error callout for them).
 *
 * Design: pure function, no I/O. The caller owns the source-file list and
 * the numbered combined-content string that the LLM was given during the
 * compile prompt, so we can both repair unambiguous single-source markers
 * and validate that repaired line references are within the source's range.
 */

import { splitCitationMarker } from "../utils/markdown.js";

/** Regex matching `^[...]` citation markers — same pattern as the viewer uses. */
const MARKER_PATTERN = /\^\[([^\]\n]+)\]/g;

/**
 * Regex matching a bare line reference with no filename component.
 * Accepts: single numbers (`81`), hyphen ranges (`81-90`), and comma lists
 * (`81, 90` or `1-5, 12`). `splitCitationMarker` keeps comma-separated
 * line lists together, so we must accept them here as a single entry.
 */
const BARE_LINE_RANGE_PATTERN = /^\d+(?:-\d+)?(?:\s*,\s*\d+(?:-\d+)?)*$/;

/** Regex matching a numbered line as emitted by buildBudgetedCombinedContent: ` N | text`. */
const NUMBERED_LINE_PATTERN = /^\s*(\d+)\s*\|/;

/**
 * Return the highest line number present in `combinedContent` (the numbered
 * source text the LLM received). Returns 0 when no numbered lines are found.
 */
function maxLineNumber(combinedContent: string): number {
  let max = 0;
  for (const line of combinedContent.split("\n")) {
    const m = NUMBERED_LINE_PATTERN.exec(line);
    if (m) {
      const n = Number(m[1]);
      if (n > max) max = n;
    }
  }
  return max;
}

/**
 * Return true when every part of a bare line entry is valid within [1, maxLine].
 *
 * An entry may be a single number ("81"), a hyphen range ("81-90"), or a
 * comma list kept together by `splitCitationMarker` ("81, 90" or "1-5, 12").
 * Each comma-separated segment is validated individually:
 * - Hyphen range `A-B`: requires A >= 1, A <= B, B <= maxLine (rejects backward).
 * - Single number `N`: requires 1 <= N <= maxLine.
 */
function rangeIsValid(entry: string, maxLine: number): boolean {
  const segments = entry.split(/\s*,\s*/);
  return segments.every((seg) => {
    const dashParts = seg.split("-").map((p) => Number(p.trim()));
    if (dashParts.length === 2) {
      const [start, end] = dashParts;
      return start >= 1 && start <= end && end <= maxLine;
    }
    const n = Number(seg.trim());
    return n >= 1 && n <= maxLine;
  });
}

/**
 * Normalise a single comma-separated entry from inside a `^[...]` marker.
 *
 * Returns the (possibly repaired) entry string, or null to signal the entry
 * should be dropped entirely.
 *
 * Bare line numbers / ranges are normalised here. Unknown filename entries
 * remain available to provenance lint during ordinary updates, but callers can
 * drop them for a clean deletion-reconciliation rebuild.
 */
function normalizeEntry(
  entry: string,
  sourceFiles: string[],
  maxLine: number,
  dropUnknownSourceEntries: boolean,
): string | null {
  const trimmed = entry.trim();
  if (trimmed.length === 0) return null;

  // Split off the file token: everything before the first `:` or `#`.
  const fileToken = trimmed.split(/[:#]/)[0].trim();

  // Case 1: valid filename — keep as-is.
  if (sourceFiles.includes(fileToken)) return trimmed;

  // Case 2: bare line reference — purely digits/range, no filename component.
  if (BARE_LINE_RANGE_PATTERN.test(trimmed)) {
    // Single source: repair if the line is in range, drop if hallucinated.
    if (sourceFiles.length === 1) {
      if (!rangeIsValid(trimmed, maxLine)) return null;
      return `${sourceFiles[0]}:${trimmed}`;
    }
    // Multi-source: ambiguous — drop (can't know which source the LLM meant).
    return null;
  }

  // Case 3: has an unknown filename component. Normal updates leave it for
  // downstream provenance lint. Clean rebuilds drop it so a removed source
  // cannot re-enter the reconciled page.
  return dropUnknownSourceEntries ? null : trimmed;
}

/**
 * Rebuild a full `^[...]` marker from its surviving entries.
 * Returns null when no entries survive (signals the whole marker should be removed).
 */
function rebuildMarker(entries: string[]): string | null {
  const survivors = entries.filter((e) => e !== null) as string[];
  if (survivors.length === 0) return null;
  return `^[${survivors.join(", ")}]`;
}

/**
 * Clean every `^[...]` citation marker in `body` before the page is written.
 *
 * For each comma-separated entry inside a marker:
 * - Valid filename entries are kept unchanged.
 * - Bare line numbers/ranges (`81`, `81-90`) on a single-source page are
 *   repaired to `<sourceFile>:N` when the line is within the source's range,
 *   or dropped when the line exceeds the source's actual line count.
 * - Bare numbers on multi-source pages are dropped (ambiguous — can't determine
 *   which source the LLM intended).
 * - Unknown filename entries remain for provenance lint by default, or are
 *   removed when `dropUnknownSourceEntries` is enabled for a clean rebuild.
 *
 * When all entries in a marker are dropped the entire `^[...]` marker is
 * removed. A trailing space before the removed marker is also collapsed so
 * `word ^[81]` becomes `word` rather than `word `.
 *
 * @param body - Raw LLM output page body to normalise.
 * @param sourceFiles - Valid source filenames for this page (basenames only).
 * @param combinedContent - The numbered combined-source text the LLM received.
 * @param dropUnknownSourceEntries - Remove citations outside sourceFiles.
 * @returns The normalised body string.
 */
export function normalizeCitations(
  body: string,
  sourceFiles: string[],
  combinedContent: string,
  dropUnknownSourceEntries = false,
): string {
  const maxLine = maxLineNumber(combinedContent);
  MARKER_PATTERN.lastIndex = 0;

  return body.replace(MARKER_PATTERN, (fullMatch, inner: string) => {
    const entries = splitCitationMarker(inner)
      .map((entry) => normalizeEntry(
        entry, sourceFiles, maxLine, dropUnknownSourceEntries,
      ));
    const rebuilt = rebuildMarker(entries as string[]);
    if (rebuilt === null) {
      // Signal for post-replace space cleanup: return empty string; the
      // space cleanup pass below handles the dangling space.
      return "\x00REMOVED\x00";
    }
    return rebuilt;
  });
}

/**
 * Run `normalizeCitations` and also clean up dangling spaces left where a
 * marker was removed. A space immediately before a removed marker is collapsed.
 *
 * This is the public entry point used by the page renderer.
 * @param body - Raw LLM output page body to normalise.
 * @param sourceFiles - Valid source filenames for this page.
 * @param combinedContent - Numbered source text supplied to the LLM.
 * @param dropUnknownSourceEntries - Remove citations outside sourceFiles.
 */
export function normalizeCitationsInBody(
  body: string,
  sourceFiles: string[],
  combinedContent: string,
  dropUnknownSourceEntries = false,
): string {
  const withSentinels = normalizeCitations(
    body,
    sourceFiles,
    combinedContent,
    dropUnknownSourceEntries,
  );
  // Remove sentinels and the optional preceding space.
  return withSentinels.replace(/ ?\x00REMOVED\x00/g, "");
}
